import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, Menu, Plus } from "lucide-react";
import { getMessageTimestampLabel } from "../../utils/sessionMessages";

function cleanDisplayLine(line = "") {
  return String(line || "")
    .replace(/^\s{0,3}#{1,6}\s+/, "")
    .replace(/\*\*([^*\n]+)\*\*/g, "$1")
    .replace(/__([^_\n]+)__/g, "$1")
    .replace(/`([^`\n]+)`/g, "$1")
    .trim();
}

function getLineListMatch(line = "") {
  const numbered = line.match(/^(\d+)[.)]\s+(.+)$/);
  if (numbered) return { type: "ol", text: numbered[2] };

  const bulleted = line.match(/^[-*•]\s+(.+)$/);
  if (bulleted) return { type: "ul", text: bulleted[1] };

  return null;
}

function renderMessageBody(text = "") {
  const lines = String(text || "")
    .replace(/\r\n?/g, "\n")
    .split("\n");
  const nodes = [];
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    const content = paragraph.map(cleanDisplayLine).filter(Boolean).join(" ");
    if (content) {
      nodes.push(
        <p key={`p-${nodes.length}`} className="focused-chat-message__paragraph">
          {content}
        </p>
      );
    }
    paragraph = [];
  };

  const flushList = () => {
    if (!list?.items?.length) {
      list = null;
      return;
    }
    const Tag = list.type === "ol" ? "ol" : "ul";
    nodes.push(
      <Tag key={`list-${nodes.length}`} className="focused-chat-message__list">
        {list.items.map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </Tag>
    );
    list = null;
  };

  lines.forEach((rawLine, index) => {
    const line = cleanDisplayLine(rawLine);
    if (!line) {
      flushParagraph();
      const nextListMatch = lines
        .slice(index + 1)
        .map(cleanDisplayLine)
        .find(Boolean);
      if (list && getLineListMatch(nextListMatch)?.type === list.type) {
        return;
      }
      flushList();
      return;
    }

    const listMatch = getLineListMatch(line);

    if (listMatch) {
      flushParagraph();
      const type = listMatch.type;
      if (!list || list.type !== type) flushList();
      if (!list) list = { type, items: [] };
      list.items.push(cleanDisplayLine(listMatch.text));
      return;
    }

    flushList();
    paragraph.push(line);
  });

  flushParagraph();
  flushList();

  return nodes.length ? nodes : null;
}

export default function FocusedChatSurface({
  sessionLabel,
  sessionMeta,
  onGoBack,
  detailsOpen = false,
  onToggleDetails,
  onStartNewSession,
  animatedAssistantMessageId = null,
  messages = [],
  loading = false,
  showTimestamps = false,
  participantLabel = "You",
  chatBoxRef,
  input = "",
  onInputChange,
  onInputKeyDown,
  onSend,
  sendDisabled = false,
  inputPlaceholder = "Type your message...",
  readOnlyHint = "",
  composerAlert = null,
  children,
}) {
  const [revealedCounts, setRevealedCounts] = useState({});
  const animationFrameRef = useRef(null);
  const previousTargetRef = useRef(null);

  useEffect(() => {
    setRevealedCounts((prev) => {
      const next = { ...prev };
      messages.forEach((msg) => {
        if (msg.sender !== "NIA") {
          next[msg.id] = msg.text.length;
          return;
        }
        if (msg.id !== animatedAssistantMessageId && typeof next[msg.id] !== "number") {
          next[msg.id] = msg.text.length;
        }
      });
      return next;
    });
  }, [messages, animatedAssistantMessageId]);

  useEffect(() => {
    if (!animatedAssistantMessageId) return undefined;
    const target = messages.find((msg) => msg.id === animatedAssistantMessageId && msg.sender === "NIA");
    if (!target) return undefined;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      setRevealedCounts((prev) => ({ ...prev, [target.id]: target.text.length }));
      previousTargetRef.current = target.id;
      return undefined;
    }

    if (previousTargetRef.current !== target.id) {
      setRevealedCounts((prev) => ({ ...prev, [target.id]: 0 }));
      previousTargetRef.current = target.id;
    }

    const total = target.text.length;
    const baseStep = total > 320 ? 5 : total > 180 ? 4 : 3;

    const tick = () => {
      setRevealedCounts((prev) => {
        const current = typeof prev[target.id] === "number" ? prev[target.id] : 0;
        if (current >= total) return prev;
        const nextCount = Math.min(total, current + baseStep);
        const next = { ...prev, [target.id]: nextCount };
        if (nextCount < total) {
          animationFrameRef.current = window.setTimeout(tick, 18);
        }
        return next;
      });
    };

    animationFrameRef.current = window.setTimeout(tick, 40);
    return () => {
      if (animationFrameRef.current) {
        window.clearTimeout(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [animatedAssistantMessageId, messages]);

  const renderedMessages = useMemo(
    () =>
      messages.map((msg) => {
        if (msg.sender !== "NIA" || msg.id !== animatedAssistantMessageId) {
          return { ...msg, renderedText: msg.text };
        }
        const count = typeof revealedCounts[msg.id] === "number" ? revealedCounts[msg.id] : 0;
        return { ...msg, renderedText: msg.text.slice(0, count) };
      }),
    [animatedAssistantMessageId, messages, revealedCounts]
  );

  return (
    <section className="focused-chat-shell" aria-busy={loading}>
      <header className="focused-chat-topbar">
        <button type="button" className="icon-btn focused-chat-back" onClick={onGoBack} aria-label="Back">
          <ChevronLeft size={18} />
        </button>
        <div className="focused-chat-topbar__meta">
          <div className="nia-subtitle">Nia</div>
          <h2>{sessionLabel}</h2>
          <p>{sessionMeta}</p>
        </div>
        <div className="focused-chat-topbar__actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onToggleDetails}
            aria-expanded={detailsOpen}
            aria-controls="chat-details-panel"
          >
            <Menu size={16} />
            {detailsOpen ? "Hide" : "Details"}
          </button>
          <button type="button" className="btn btn-ghost focused-chat-topbar__new" onClick={onStartNewSession}>
            <Plus size={16} />
            New
          </button>
        </div>
      </header>

      {children ? <div className="focused-chat-stack">{children}</div> : null}

      <div className="focused-chat-thread">
        <div ref={chatBoxRef} className="chat-box focused-chat-box">
          {renderedMessages.map((msg) => {
            const isUser = msg.sender !== "NIA";
            const messageLabel = isUser ? participantLabel : "Nia";
            const timestampLabel = getMessageTimestampLabel(msg);
            return (
              <div key={msg.id} className={`chat-row ${isUser ? "user" : ""}`}>
                <div className={`bubble ${isUser ? "user" : "assistant"}`}>
                  <div className="focused-chat-message__meta">
                    <span className="focused-chat-message__author">{messageLabel}</span>
                    {showTimestamps && timestampLabel ? (
                      <span className="focused-chat-message__timestamp">{timestampLabel}</span>
                    ) : null}
                  </div>
                  <div className="focused-chat-message__body">{renderMessageBody(msg.renderedText)}</div>
                </div>
              </div>
            );
          })}
          {loading ? (
            <p className="focused-chat-typing">
              <em>Nia is typing...</em>
            </p>
          ) : null}
        </div>
      </div>

      <div className="focused-chat-composer">
        {composerAlert ? <div className="focused-chat-composer-alert">{composerAlert}</div> : null}
        <textarea
          rows="3"
          className="chat-input focused-chat-input"
          value={input}
          disabled={sendDisabled}
          onChange={(e) => onInputChange?.(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder={inputPlaceholder}
        />
        <div className="focused-chat-composer__footer">
          <div className="focused-chat-composer__hint">
            Press <strong>Enter</strong> to send, <strong>Shift + Enter</strong> for a new line.
            {readOnlyHint ? ` ${readOnlyHint}` : ""}
          </div>
          <button
            type="button"
            className="btn btn-primary focused-chat-send"
            onClick={onSend}
            disabled={sendDisabled}
          >
            {loading ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </section>
  );
}
